import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  Upload, FileText, Plus, Trash2, Check, AlertTriangle, XCircle, Loader2, Receipt, ScanLine, Landmark, RotateCcw,
} from 'lucide-react';
import { useConfirm } from '../../components/ConfirmDialog';
import PaySuppliers from './PaySuppliers';
import { isoDate } from '../../utils/time';
import {
  PageHead, Panel, Register, FigureStrip, Figure, DetailDrawer, DataTable,
  AcStatus, Currency, Field, FormRow, fmtDate, money,
} from './design';

const PAYMENT_TYPES = ['Supplier payment', 'Client reimbursement'];
// Anything but a client reimbursement is paid to the supplier (the pay run reads it the same way),
// so an older or imported value never shows as a raw code.
const paymentOf = (v) => (v === 'Client reimbursement' ? v : 'Supplier payment');

const schemeLabel = (scheme) => (String(scheme || '').toUpperCase() === 'HCP' ? 'Home Care Package' : 'Support at Home');

const emptyLine = () => ({ description: '', qty: 1, rate: '', gst: 0, lineTotal: 0 });

const recalcLine = (qty, rate, gst) => Number(qty || 0) * Number(rate || 0) + Number(gst || 0);

// `clientId` is the participant's record. `namedClient` is a name the invoice carries without that
// link (read from a PDF, or typed on an older invoice), kept so it is not lost and shown so the
// operator can pick the right person. `clientKnown` says whether the picker reflects the server:
// only then is the participant sent on save, so opening an invoice the list did not describe fully
// can never unlink its participant by accident.
const emptyReview = () => ({
  supplier: '',
  clientId: '',
  clientName: '',
  namedClient: '',
  clientKnown: true,
  scheme: 'SAH',
  invoiceNumber: '',
  date: isoDate(),
  serviceFrom: '',
  serviceTo: '',
  paymentType: 'Supplier payment',
  total: '',
  lineItems: [emptyLine()],
});

// The list answers in camelCase and a saved row by its column names, so each field is read from
// either shape.
const ymd = (d) => (d ? String(d).slice(0, 10) : '');
const hasClientField = (inv) => 'clientId' in inv || 'client_id' in inv;
const clientIdOf = (inv) => inv.clientId || inv.client_id || '';
const isPaid = (inv) => inv.status === 'Paid' || !!(inv.paid_at || inv.paidAt);
const isMatched = (inv) => !!(inv.poMatch ?? inv.po_match) || !!(inv.po_id || inv.poId);
const poLabel = (inv) => inv.po_number || inv.poNumber || null;
const rejectionOf = (inv) => inv.rejection_reason || inv.rejectionReason || '';
const numberOf = (inv) => inv.invoiceNumber || inv.invoice_number || '';
// The server takes changes while an invoice is pending, a draft or rejected. An approved one goes
// back to pending first (which undoes its drawdown), and a paid one never changes.
const isEditable = (inv) => !isPaid(inv) && !['Approved', 'Paid'].includes(inv.status);
const linesOf = (inv) => {
  let li = inv.lineItems ?? inv.line_items;
  if (typeof li === 'string') { try { li = JSON.parse(li); } catch { li = []; } }
  return Array.isArray(li) ? li : [];
};
const toLine = (li) => ({
  description: li.description || '',
  qty: li.qty ?? 1,
  rate: li.rate ?? '',
  gst: li.gst ?? 0,
  lineTotal: li.lineTotal ?? recalcLine(li.qty ?? 1, li.rate ?? 0, li.gst ?? 0),
});

// A name read from a PDF or typed on an older invoice is matched to a participant only when it is
// exactly their name (ignoring case and spacing); anything looser could pick the wrong person.
const norm = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
const findClient = (clients, name) => {
  const n = norm(name);
  return n ? clients.find((c) => norm(c.name) === n) || null : null;
};

const input = 'input-field !rounded-lg !px-2 !py-1.5 !text-xs';

// Row actions sit inside a clickable row: without this a click on Approve would also open the
// invoice, and Enter on the button would be taken by the row.
const stop = (e) => e.stopPropagation();

export default function AgedCareSupplierInvoices() {
  const confirm = useConfirm();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientsError, setClientsError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [review, setReview] = useState(emptyReview());
  // The invoice open in the drawer, or null while a new one is being entered.
  const [openInv, setOpenInv] = useState(null);
  // Whether the open review came from a scanned PDF or was started by hand. It changes nothing
  // about the save, only what the drawer tells the operator they are checking.
  const [extracted, setExtracted] = useState(false);
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const fileRef = React.useRef(null);
  // Stable, or the drawer re-runs its focus effect on every keystroke in the review form and pulls
  // focus out of the field being typed in.
  const closeReview = useCallback(() => setShowReview(false), []);
  const closeReject = useCallback(() => setRejecting(null), []);

  // `quiet` refreshes the list after an action without flashing the skeleton over it.
  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const r = await api.get('/aged-care/supplier-invoices');
      setInvoices(Array.isArray(r.data) ? r.data : (r.data?.invoices || []));
      setLoadError(null);
    } catch (e) {
      setLoadError(e.response?.data?.message || 'Could not load the supplier invoices');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadClients = useCallback(async () => {
    setClientsError(null);
    try {
      const r = await api.get('/aged-care/clients');
      const list = Array.isArray(r.data) ? r.data : (r.data?.clients || []);
      setClients([...list].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'en-AU')));
    } catch (e) {
      setClientsError(e.response?.data?.message || 'Could not load the participants');
    }
  }, []);

  useEffect(() => { load(); loadClients(); }, [load, loadClients]);

  function pickFile() {
    if (fileRef.current) fileRef.current.value = '';
    fileRef.current?.click();
  }

  async function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post('/aged-care/supplier-invoices/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const ex = r.data || {};
      const lines = Array.isArray(ex.lineItems) && ex.lineItems.length ? ex.lineItems.map(toLine) : [emptyLine()];
      const match = findClient(clients, ex.client);
      setReview({
        ...emptyReview(),
        supplier: ex.supplier || '',
        clientId: match ? String(match.id) : '',
        clientName: match ? match.name : '',
        namedClient: match ? '' : (ex.client || ''),
        scheme: ex.scheme || 'SAH',
        invoiceNumber: ex.invoiceNumber || '',
        date: (ex.date || new Date().toISOString()).slice(0, 10),
        serviceFrom: ex.serviceFrom ? ex.serviceFrom.slice(0, 10) : '',
        serviceTo: ex.serviceTo ? ex.serviceTo.slice(0, 10) : '',
        paymentType: ex.paymentType || 'Supplier payment',
        total: ex.total ?? '',
        lineItems: lines,
      });
      setOpenInv(null);
      setExtracted(true);
      setShowReview(true);
      toast.success('Invoice read. Check the details before saving.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not read that invoice. Try a clearer PDF, or enter it by hand.');
    } finally {
      setImporting(false);
    }
  }

  function manualEntry() {
    setReview(emptyReview());
    setOpenInv(null);
    setExtracted(false);
    setShowReview(true);
  }

  function openInvoice(inv) {
    const lines = linesOf(inv).map(toLine);
    const id = clientIdOf(inv);
    // An invoice whose participant the list does not say: try the name it carries.
    const match = !id ? findClient(clients, inv.client) : null;
    setReview({
      ...emptyReview(),
      supplier: inv.supplier || '',
      clientId: id ? String(id) : (match ? String(match.id) : ''),
      clientName: inv.client || '',
      namedClient: id || match ? '' : (inv.client || ''),
      clientKnown: hasClientField(inv) || !!match,
      scheme: String(inv.scheme || '').toUpperCase() === 'HCP' ? 'HCP' : 'SAH',
      invoiceNumber: numberOf(inv),
      date: ymd(inv.date || inv.invoice_date),
      serviceFrom: ymd(inv.serviceFrom || inv.service_from),
      serviceTo: ymd(inv.serviceTo || inv.service_to),
      paymentType: paymentOf(inv.paymentType || inv.payment_type),
      total: inv.total ?? '',
      lineItems: lines.length ? lines : [emptyLine()],
    });
    setOpenInv(inv);
    setExtracted(false);
    setShowReview(true);
  }

  function updateLine(idx, field, value) {
    setReview(r => {
      const lineItems = r.lineItems.map((li, i) => {
        if (i !== idx) return li;
        const next = { ...li, [field]: value };
        next.lineTotal = recalcLine(next.qty, next.rate, next.gst);
        return next;
      });
      return { ...r, lineItems };
    });
  }

  function addLine() {
    setReview(r => ({ ...r, lineItems: [...r.lineItems, emptyLine()] }));
  }

  function removeLine(idx) {
    setReview(r => ({
      ...r,
      lineItems: r.lineItems.length > 1 ? r.lineItems.filter((_, i) => i !== idx) : r.lineItems,
    }));
  }

  const linesTotal = review.lineItems.reduce((s, li) => s + Number(li.lineTotal || 0), 0);
  const totalDiffers = review.total !== '' && Math.abs(Number(review.total) - linesTotal) > 0.005;
  const readOnly = !!openInv && !isEditable(openInv);
  // A reimbursement is paid into the participant's own account, so it cannot go without one.
  const needsParticipant = review.clientKnown && review.paymentType === 'Client reimbursement' && !review.clientId;

  async function saveInvoice() {
    if (!review.supplier.trim()) return toast.error('Enter the supplier before saving');
    if (!review.invoiceNumber.trim()) return toast.error('Enter the invoice number before saving');
    if (needsParticipant) return toast.error('Choose the participant to reimburse before saving');
    setSaving(true);
    try {
      const body = {
        supplier: review.supplier.trim(),
        scheme: review.scheme,
        invoiceNumber: review.invoiceNumber.trim(),
        date: review.date || null,
        serviceFrom: review.serviceFrom || null,
        serviceTo: review.serviceTo || null,
        paymentType: review.paymentType,
        total: review.total !== '' ? Number(review.total) : linesTotal,
        lineItems: review.lineItems.map(li => ({
          description: li.description,
          qty: Number(li.qty || 0),
          rate: Number(li.rate || 0),
          gst: Number(li.gst || 0),
          lineTotal: Number(li.lineTotal || 0),
        })),
      };
      if (review.clientKnown) {
        body.clientId = review.clientId || null;
        body.client = review.clientId ? null : (review.namedClient.trim() || null);
      }
      // The server works out the purchase order match on every save; say what it found.
      const r = openInv
        ? await api.put(`/aged-care/supplier-invoices/${openInv.id}`, body)
        : await api.post('/aged-care/supplier-invoices', body);
      const saved = openInv ? 'Changes saved' : 'Invoice saved';
      toast.success(isMatched(r.data || {})
        ? `${saved}. It matches a purchase order.`
        : `${saved}. No purchase order matches it, so it is held for review.`);
      setShowReview(false);
      load({ quiet: true });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not save the invoice');
    } finally {
      setSaving(false);
    }
  }

  async function changeStatus(inv, status, reason) {
    setBusyId(inv.id);
    try {
      const r = await api.patch(`/aged-care/supplier-invoices/${inv.id}`, reason ? { status, reason } : { status });
      const ci = r.data?.claimItem || null;
      if (status === 'Approved') {
        toast.success(ci && ci.id && !ci.existing ? 'Invoice approved. The claim item was made from it.' : 'Invoice approved');
        if (ci && ci.error) toast.error(ci.error, { duration: 8000 });
        if (ci && ci.warning) toast(ci.warning, { duration: 8000 });
      } else if (status === 'Rejected') {
        toast.success('Invoice rejected');
      } else {
        toast.success(ci && ci.removed ? 'Invoice put back to pending. The claim item made from it was removed.' : 'Invoice put back to pending');
      }
      load({ quiet: true });
      return r.data || {};
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not update the invoice');
      return null;
    } finally {
      setBusyId(null);
    }
  }

  async function approve(inv) {
    const po = poLabel(inv);
    const ok = await confirm(
      isMatched(inv)
        ? `This draws ${money(inv.total)} down from ${po ? `purchase order ${po}` : 'the purchase order it matches'}. If that order buys an item for a Support at Home claim, the claim item is made from this invoice. The invoice is then ready to pay.`
        : 'No purchase order matches this invoice, so nothing is drawn down and no claim item is made. The invoice is then ready to pay.',
      { title: numberOf(inv) ? `Approve invoice ${numberOf(inv)}?` : 'Approve this invoice?', confirmLabel: 'Approve invoice' },
    );
    if (ok) await changeStatus(inv, 'Approved');
  }

  function startReject(inv) {
    setRejectReason('');
    setRejecting(inv);
  }

  async function submitReject() {
    if (!rejecting) return;
    if (!rejectReason.trim()) return toast.error('Say why the invoice is rejected');
    const done = await changeStatus(rejecting, 'Rejected', rejectReason.trim());
    if (done) setRejecting(null);
  }

  // Back to pending: from Approved it reverses the purchase order drawdown and removes the claim
  // item made from the invoice, so it asks first; from Rejected nothing has moved, so it does not.
  async function putBack(inv) {
    if (inv.status === 'Approved') {
      const ok = await confirm(
        isMatched(inv)
          ? `This reverses the ${money(inv.total)} drawn down from its purchase order and removes the claim item made from it, so the invoice can be corrected and approved again.`
          : 'It comes off the list to pay, so it can be corrected and approved again.',
        { title: numberOf(inv) ? `Put invoice ${numberOf(inv)} back to pending?` : 'Put this invoice back to pending?', confirmLabel: 'Put back to pending', danger: true },
      );
      if (!ok) return;
    }
    const done = await changeStatus(inv, 'Pending');
    // Open in the drawer: it becomes editable straight away, which is why it was put back.
    if (done && openInv && openInv.id === inv.id) setOpenInv({ ...inv, status: 'Pending', rejection_reason: null, rejectionReason: null });
  }

  // ---- stats ----
  const pendingCount = invoices.filter(i => i.status === 'Pending').length;
  const approvedCount = invoices.filter(i => i.status === 'Approved').length;
  const pendingValue = invoices
    .filter(i => i.status === 'Pending')
    .reduce((s, i) => s + Number(i.total || 0), 0);
  const noPoCount = invoices.filter(i => !isMatched(i)).length;

  // The participant chosen on an invoice may not be in the list (for one, if the list failed to
  // load); it is still offered so the picker shows who it is.
  const clientOptions = review.clientId && !clients.some(c => String(c.id) === String(review.clientId))
    ? [{ id: review.clientId, name: review.clientName || 'The participant on this invoice' }, ...clients]
    : clients;

  const columns = [
    {
      key: 'supplier', header: 'Supplier', width: 220, sortable: true, truncate: true,
      render: (inv) => <span className="block truncate font-medium text-slate-800">{inv.supplier || '—'}</span>,
    },
    {
      key: 'client', header: 'Participant', width: 190, sortable: true, truncate: true,
      render: (inv) => (
        <span className="block min-w-0">
          <span className="block truncate text-slate-700">{inv.client || 'Not for a participant'}</span>
          <span className="block text-[11px] text-slate-500 truncate">{schemeLabel(inv.scheme)}</span>
        </span>
      ),
    },
    { key: 'invoiceNumber', header: 'Invoice number', width: 140, sortable: true, render: (inv) => numberOf(inv) || '—' },
    {
      key: 'date', header: 'Invoice date', width: 120, sortable: true,
      render: (inv) => <span className="tabular-nums text-slate-500 whitespace-nowrap">{fmtDate(inv.date)}</span>,
    },
    {
      key: 'total', header: 'Total', money: true, sortable: true,
      render: (inv) => <Currency value={inv.total} className="font-medium text-slate-900" />,
    },
    { key: 'paymentType', header: 'Payment', width: 150, sortable: true, render: (inv) => <span className="whitespace-nowrap">{paymentOf(inv.paymentType)}</span> },
    {
      key: 'poMatch', header: 'Purchase order', width: 140, sortable: true,
      sortValue: (inv) => (isMatched(inv) ? poLabel(inv) || 'Matched' : null),
      render: (inv) => (isMatched(inv)
        ? <span className="text-slate-700">{poLabel(inv) || 'Matched'}</span>
        : <span className="text-amber-700">Not matched</span>),
    },
    {
      key: 'status', header: 'Status', width: 150, sortable: true,
      render: (inv) => (
        <span className="block min-w-0">
          <AcStatus status={inv.status || 'Draft'} />
          {inv.status === 'Rejected' && rejectionOf(inv) && (
            <span className="block max-w-[9rem] truncate text-[11px] text-red-800 mt-0.5" title={rejectionOf(inv)}>{rejectionOf(inv)}</span>
          )}
        </span>
      ),
    },
  ];

  const lineColumns = [
    {
      key: 'description', header: 'Description',
      render: (li, idx) => (
        <input
          className={input} value={li.description} aria-label="Description"
          onChange={e => updateLine(idx, 'description', e.target.value)}
          placeholder="Service or item"
        />
      ),
    },
    {
      key: 'qty', header: 'Quantity', width: 80, align: 'right',
      render: (li, idx) => (
        <input
          type="number" min="0" className={`${input} text-right`} value={li.qty} aria-label="Quantity"
          onChange={e => updateLine(idx, 'qty', e.target.value)}
        />
      ),
    },
    {
      key: 'rate', header: 'Rate', width: 100, align: 'right',
      render: (li, idx) => (
        <input
          type="number" min="0" step="0.01" className={`${input} text-right`} value={li.rate} aria-label="Rate"
          onChange={e => updateLine(idx, 'rate', e.target.value)}
        />
      ),
    },
    {
      key: 'gst', header: 'GST', width: 100, align: 'right',
      render: (li, idx) => (
        <input
          type="number" min="0" step="0.01" className={`${input} text-right`} value={li.gst} aria-label="GST"
          onChange={e => updateLine(idx, 'gst', e.target.value)}
        />
      ),
    },
    {
      key: 'lineTotal', header: 'Line total', width: 110, money: true,
      render: (li) => <Currency value={li.lineTotal} className="font-medium text-slate-900" />,
    },
  ];

  // What the drawer says about itself, by what the operator can do with the invoice.
  let drawerTitle; let drawerSubtitle; let drawerIcon = FileText;
  if (!openInv) {
    drawerTitle = extracted ? 'Check the extracted invoice' : 'New supplier invoice';
    drawerSubtitle = extracted
      ? 'These details were read from the PDF. Correct anything that is wrong before saving.'
      : 'Enter the invoice as it appears on the supplier’s bill.';
    if (extracted) drawerIcon = ScanLine;
  } else if (readOnly) {
    drawerTitle = numberOf(openInv) ? `Invoice ${numberOf(openInv)}` : 'Supplier invoice';
    drawerSubtitle = isPaid(openInv)
      ? 'Paid to the supplier, so it can no longer change.'
      : 'Approved, so it cannot be changed. Put it back to pending to correct it.';
  } else {
    drawerTitle = 'Edit invoice';
    drawerSubtitle = openInv.status === 'Rejected'
      ? 'Correct the invoice, then put it back to pending so it can be approved.'
      : 'Correct anything that is wrong, then save. It stays waiting for approval.';
  }

  let participantNote = null;
  if (needsParticipant) participantNote = { error: 'A client reimbursement is paid into the participant’s own account, so choose the participant.' };
  else if (clientsError) participantNote = { error: clientsError };
  else if (review.namedClient && !review.clientId) participantNote = { hint: `The invoice names “${review.namedClient}”. Choose them from the list to link the invoice to their record.` };
  else participantNote = { hint: 'Choose “Not for a participant” if the invoice is not for one person.' };

  return (
    <div>
      <PaySuppliers open={payOpen} onClose={() => setPayOpen(false)} onDone={() => load({ quiet: true })} />
      <input
        ref={fileRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={onFile}
      />

      <PageHead icon={Receipt}
        title="Supplier invoices"
        purpose="Invoices from third-party providers arrive here. Import a PDF and its details are read for you, then check them against the purchase order and approve the invoice for payment."
        actions={(
          <>
            <button type="button" onClick={() => setPayOpen(true)} className="btn-secondary">
              <Landmark size={15} /> Pay approved
            </button>
            <button type="button" onClick={manualEntry} className="btn-secondary">
              <Plus size={15} /> Enter by hand
            </button>
            <button type="button" onClick={pickFile} disabled={importing} className="btn-primary">
              {importing ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
              {importing ? 'Reading the invoice' : 'Import a PDF invoice'}
            </button>
          </>
        )}
      />

      {/* Hidden while the list failed to load: four zeros would read as "nothing waiting". */}
      {!loadError && (
        <FigureStrip>
          <Figure label="Waiting for approval" value={pendingCount} tone={pendingCount ? 'amber' : 'muted'} />
          <Figure label="Value waiting for approval" value={money(pendingValue)} />
          <Figure label="Approved" value={approvedCount} />
          <Figure label="Without a purchase order" value={noPoCount} tone={noPoCount ? 'amber' : 'muted'} />
        </FigureStrip>
      )}

      <Register
        title="Invoice queue"
        count={invoices.length}
        countLabel={invoices.length === 1 ? 'invoice' : 'invoices'}
        columns={columns}
        rows={invoices}
        rowKey="id"
        loading={loading}
        error={loadError}
        onRetry={() => load()}
        onRowClick={openInvoice}
        activeRowKey={showReview && openInv ? openInv.id : null}
        rowTone={(inv) => (inv.status === 'Pending' && !isMatched(inv) ? 'amber' : null)}
        rowActions={(inv) => {
          const busy = busyId === inv.id;
          if (inv.status === 'Pending') {
            return (
              <span className="flex items-center gap-1" onClick={stop} onKeyDown={stop}>
                <button type="button" onClick={() => approve(inv)} disabled={busy} className="btn-secondary btn-sm">
                  <Check size={13} /> Approve
                </button>
                <button type="button" onClick={() => startReject(inv)} disabled={busy} className="btn-ghost btn-sm hover:text-red-700">
                  <XCircle size={13} /> Reject
                </button>
              </span>
            );
          }
          if (inv.status === 'Rejected') {
            return (
              <span className="flex items-center gap-1" onClick={stop} onKeyDown={stop}>
                <button type="button" onClick={() => putBack(inv)} disabled={busy} className="btn-ghost btn-sm">
                  <RotateCcw size={13} /> Put back to pending
                </button>
              </span>
            );
          }
          return null;
        }}
        empty={{
          icon: Receipt,
          title: 'No supplier invoices yet',
          hint: 'Import a PDF and the supplier, dates, totals and line items are read from it, ready for you to check before saving.',
          action: (
            <button type="button" onClick={pickFile} disabled={importing} className="btn-primary btn-sm">
              <Upload size={14} /> Import a PDF invoice
            </button>
          ),
        }}
        footer={`${noPoCount} of ${invoices.length} invoice${invoices.length === 1 ? '' : 's'} ${noPoCount === 1 ? 'has' : 'have'} no matching purchase order. An invoice without one is held until someone approves it. Open an invoice to see or correct it.`}
      />

      {showReview && (
        <DetailDrawer
          open
          onClose={closeReview}
          icon={drawerIcon}
          width="xl"
          title={drawerTitle}
          subtitle={drawerSubtitle}
          statusNode={openInv ? <AcStatus status={openInv.status || 'Draft'} /> : null}
          meta={[
            { label: 'Lines subtotal', value: money(linesTotal), money: true },
            {
              label: 'Purchase order',
              value: !openInv ? 'Checked when you save' : (isMatched(openInv) ? (poLabel(openInv) || 'Matched') : 'Not matched'),
            },
          ]}
          footer={readOnly ? (
            <>
              <button type="button" onClick={closeReview} className="btn-secondary">Close</button>
              {openInv.status === 'Approved' && !isPaid(openInv) && (
                <button type="button" onClick={() => putBack(openInv)} disabled={busyId === openInv.id} className="btn-primary">
                  <RotateCcw size={15} /> Put back to pending
                </button>
              )}
            </>
          ) : (
            <>
              <button type="button" onClick={closeReview} className="btn-secondary">Cancel</button>
              <button type="button" onClick={saveInvoice} disabled={saving} className="btn-primary">
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                {saving ? 'Saving' : (openInv ? 'Save changes' : 'Save invoice')}
              </button>
            </>
          )}
        >
          {/* One fieldset, so a read-only invoice shows the same form with every control locked. */}
          <fieldset disabled={readOnly} className="min-w-0 space-y-4">
            {openInv && openInv.status === 'Rejected' && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-800">
                {rejectionOf(openInv) ? <>Rejected: {rejectionOf(openInv)}</> : 'Rejected. No reason was recorded.'}
              </p>
            )}

            <Panel title="Invoice details" bodyClass="px-4 py-4">
              <div className="space-y-3.5">
                <FormRow cols={2}>
                  <Field label="Supplier" required>
                    {(id) => (
                      <input
                        id={id} className="input-field" value={review.supplier}
                        onChange={e => setReview(r => ({ ...r, supplier: e.target.value }))}
                        placeholder="Northside Allied Health"
                      />
                    )}
                  </Field>
                  <Field
                    label="Participant"
                    required={review.paymentType === 'Client reimbursement'}
                    error={participantNote.error}
                    hint={participantNote.hint}
                    action={clientsError && !readOnly ? (
                      <button type="button" onClick={loadClients} className="text-[12px] text-primary-700 hover:underline">Try again</button>
                    ) : null}
                  >
                    {(id) => (
                      <select
                        id={id} className="input-field" value={review.clientId}
                        onChange={e => {
                          const c = clientOptions.find(x => String(x.id) === e.target.value);
                          setReview(r => ({ ...r, clientId: e.target.value, clientName: c ? c.name : '', clientKnown: true }));
                        }}
                      >
                        <option value="">Not for a participant</option>
                        {clientOptions.map(c => <option key={c.id} value={String(c.id)}>{c.name || 'Participant'}</option>)}
                      </select>
                    )}
                  </Field>
                </FormRow>
                <FormRow cols={2}>
                  <Field label="Invoice number" required>
                    {(id) => (
                      <input
                        id={id} className="input-field" value={review.invoiceNumber}
                        onChange={e => setReview(r => ({ ...r, invoiceNumber: e.target.value }))}
                        placeholder="INV-0001"
                      />
                    )}
                  </Field>
                  <Field label="Invoice date">
                    {(id) => (
                      <input
                        id={id} type="date" className="input-field" value={review.date}
                        onChange={e => setReview(r => ({ ...r, date: e.target.value }))}
                      />
                    )}
                  </Field>
                </FormRow>
                <FormRow cols={2}>
                  <Field label="Service from">
                    {(id) => (
                      <input
                        id={id} type="date" className="input-field" value={review.serviceFrom}
                        onChange={e => setReview(r => ({ ...r, serviceFrom: e.target.value }))}
                      />
                    )}
                  </Field>
                  <Field label="Service to">
                    {(id) => (
                      <input
                        id={id} type="date" className="input-field" value={review.serviceTo}
                        onChange={e => setReview(r => ({ ...r, serviceTo: e.target.value }))}
                      />
                    )}
                  </Field>
                </FormRow>
                <FormRow cols={2}>
                  <Field label="Scheme">
                    {(id) => (
                      <select
                        id={id} className="input-field" value={review.scheme}
                        onChange={e => setReview(r => ({ ...r, scheme: e.target.value }))}
                      >
                        <option value="SAH">Support at Home</option>
                        <option value="HCP">Home Care Package</option>
                      </select>
                    )}
                  </Field>
                  <Field label="Payment type" hint={review.paymentType === 'Client reimbursement' ? 'Paid to the participant, who paid the supplier themselves.' : undefined}>
                    {(id) => (
                      <select
                        id={id} className="input-field" value={review.paymentType}
                        onChange={e => setReview(r => ({ ...r, paymentType: e.target.value }))}
                      >
                        {PAYMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    )}
                  </Field>
                </FormRow>
              </div>
            </Panel>

            <Panel title="Purchase order" bodyClass="px-4 py-3.5">
              {!openInv ? (
                <p className="text-[13px] text-slate-700">Checked when you save.</p>
              ) : isMatched(openInv) ? (
                <p className="flex items-start gap-1.5 text-[13px] text-slate-700">
                  <Check size={14} className="mt-0.5 shrink-0 text-emerald-600" aria-hidden="true" />
                  <span>
                    Matched to {poLabel(openInv) ? `purchase order ${poLabel(openInv)}` : 'a purchase order'}.
                    {openInv.status === 'Approved' || isPaid(openInv) ? ' Its total has been drawn down from that order.' : ' Approving the invoice draws its total down from that order.'}
                  </span>
                </p>
              ) : (
                <p className="flex items-start gap-1.5 text-[13px] text-amber-800">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
                  No purchase order matches this invoice, so it is held for someone to review before it is approved.
                </p>
              )}
              <p className="text-[11.5px] text-slate-500 mt-1.5">
                Matching uses the provider name, the amount and the date: an open purchase order with a provider whose
                name matches the supplier, enough balance left to cover the total, and effective dates that include the
                invoice date. It is checked again each time the invoice is saved.
              </p>
            </Panel>

            <Panel
              title="Line items"
              actions={readOnly ? null : (
                <button type="button" onClick={addLine} className="btn-ghost btn-sm">
                  <Plus size={14} /> Add a line
                </button>
              )}
            >
              <DataTable
                columns={lineColumns}
                rows={review.lineItems}
                rowKey={(li, i) => i}
                dense
                stickyHeader={false}
                label="Line items"
                rowActions={readOnly ? undefined : (li, idx) => (
                  <button
                    type="button"
                    onClick={() => removeLine(idx)}
                    disabled={review.lineItems.length <= 1}
                    className="btn-ghost btn-sm hover:text-red-700 disabled:opacity-30"
                    aria-label="Remove this line"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
                empty={{ icon: Receipt, title: 'No line items', hint: 'Add a line to itemise what the supplier billed for.' }}
                footer={<span>Lines subtotal <span className="font-semibold text-slate-800 tabular-nums">{money(linesTotal)}</span></span>}
              />
            </Panel>

            <Panel title="Invoice total" bodyClass="px-4 py-4">
              <div className="max-w-xs">
                <Field
                  label="Invoice total"
                  hint="Leave it blank to use the lines subtotal."
                  error={totalDiffers ? `This differs from the lines subtotal of ${money(linesTotal)}. Check the lines before saving.` : undefined}
                >
                  {(id) => (
                    <input
                      id={id} type="number" min="0" step="0.01" className="input-field" value={review.total}
                      onChange={e => setReview(r => ({ ...r, total: e.target.value }))}
                      placeholder={String(linesTotal.toFixed(2))}
                    />
                  )}
                </Field>
                {totalDiffers && (
                  <p className="text-[11.5px] text-amber-700 mt-1.5 flex items-start gap-1">
                    <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                    The invoice will be saved with the total you typed, not the lines subtotal.
                  </p>
                )}
              </div>
            </Panel>
          </fieldset>
        </DetailDrawer>
      )}

      {rejecting && (
        <DetailDrawer
          open
          onClose={closeReject}
          icon={XCircle}
          width="sm"
          title={numberOf(rejecting) ? `Reject invoice ${numberOf(rejecting)}` : 'Reject this invoice'}
          subtitle={`${rejecting.supplier || 'Supplier'}, ${money(rejecting.total)}`}
          footer={(
            <>
              <button type="button" onClick={closeReject} className="btn-secondary">Cancel</button>
              <button type="button" onClick={submitReject} disabled={busyId === rejecting.id || !rejectReason.trim()} className="btn-danger">
                <XCircle size={15} /> {busyId === rejecting.id ? 'Rejecting' : 'Reject invoice'}
              </button>
            </>
          )}
        >
          <div className="space-y-3">
            <p className="text-[13px] text-slate-600">
              A rejected invoice is not paid. Its reason shows on the invoice, so whoever corrects it knows what to fix.
            </p>
            <Field label="Why is it rejected?" required>
              {(id) => (
                <textarea
                  id={id} rows={4} className="input-field" value={rejectReason} maxLength={500}
                  onChange={e => setRejectReason(e.target.value)}
                  placeholder="For example: billed for a visit that did not happen"
                />
              )}
            </Field>
          </div>
        </DetailDrawer>
      )}
    </div>
  );
}
